/**
 * CONNECTION RUNBOOK do marketdata (bloco session-transport 🔴) — extraído de
 * marketdata.jsx na modularização dos god-objects: runConnection (build da
 * sessão SignalR + handlers Q/HB/NM/close + token refresh proativo),
 * startUsingCredentials/startUsingAuth (auth com failover multi-servidor),
 * stop, política de reconexão (backoff exponencial com equal-jitter + cap,
 * classificação recoverable × auth-fatal, eviction sticky), handlers de
 * disconnect/socket-close, checkConnectivity, reloadAllChartsData e
 * reregisterAllSymbols.
 *
 * State machine de sessão: corpos preservados byte-a-byte, `this` → `md`;
 * estado NA INSTÂNCIA; chamadas internas via `md.<método>` (delegates) —
 * identity guards e ordem de eventos EXATAMENTE os mesmos. MOVER, nunca
 * reestruturar aqui.
 *
 * Contratos: marketdataConnectionPolicyContract.test.js (backoff/classificação/
 * stop) + execução REAL — todo login de e2e roda este runbook ponta-a-ponta.
 */
import * as signalR from "@microsoft/signalr";
import { MessagePackHubProtocol } from "@microsoft/signalr-protocol-msgpack";
import * as utilities from "./utilites";
import { parsedMarketdataUrls } from "./settings";
import logger from "../../utils/logger";
import { eventBus, EVENTS } from "../../utils/eventBus";
import timeoutManager from "../../utils/timeoutManager";
import { urlManager } from "../../utils/urlManager";
import { PRODUCT_NAME, PRODUCT_VERSION } from "../../constants/product";

const AUTH_COMMAND = "/api/security/auth";
const RT_COMMAND = "/rt";

// #304 sticky eviction — persistida em sessionStorage p/ sobreviver a F5 (a
// aba expulsa só volta fechando e abrindo outra). Exportado: o constructor do
// Marketdata lê a flag no boot.
const EVICTED_STORAGE_KEY = "marketdata_evicted";
export function readEvictedFlag() {
  try {
    return (
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(EVICTED_STORAGE_KEY) === "1"
    );
  } catch {
    return false;
  }
}
export function writeEvictedFlag() {
  try {
    if (typeof sessionStorage !== "undefined")
      sessionStorage.setItem(EVICTED_STORAGE_KEY, "1");
  } catch {
    /* sessionStorage unavailable — flag stays in-memory only */
  }
}

export const runConnection = async (md) => {
  const connectionStartTime = new Date().toISOString();
  console.log(
    `[${connectionStartTime}] 🔧 Starting SignalR connection process`,
  );
  console.log(`[${connectionStartTime}] 📡 Target server: ${md.getUrl()}`);
  console.log(`[${connectionStartTime}] 🏢 Broker: ${md.broker}`);

  // Log para o usuário - apenas em reconexões
  if (md.reconnectionAttempts > 0) {
    logger.connection.info("Conectando ao servidor de dados", {
      server: md.getUrl(),
    });
  }

  try {
    console.log(
      `[${new Date().toISOString()}] 🔑 Refreshing authentication token...`,
    );
    await md.refreshToken();

    console.log(
      `[${new Date().toISOString()}] 📊 Loading symbols (with cache support)...`,
    );
    await md.getSymbols(null, { useCache: true, backgroundRefresh: true });

    const options = {
      accessTokenFactory: () => md.getValidToken(),
    };
    const rtserver = `${md.getUrl()}${RT_COMMAND}`;

    console.log(
      `[${new Date().toISOString()}] 🏗️  Building SignalR connection to: ${rtserver}`,
    );
    // C2: nunca sobrescrever um socket vivo. Captura a conexão anterior, promove a nova como
    // atual, e então para/destaca a antiga — senão ela fica órfã (socket aberto) e seu onclose
    // dispara um reconnect concorrente. O guard de identidade no onclose (abaixo) fecha o race.
    const previous = md.signalRConnection;

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(rtserver, options)
      .withHubProtocol(new MessagePackHubProtocol())
      .configureLogging(signalR.LogLevel.Error)
      .build();
    md.signalRConnection = conn;

    if (previous && previous !== conn && typeof previous.stop === "function") {
      try {
        previous.off("HB");
        previous.off("Q");
        previous.off("B");
        previous.off("NM");
        previous.off("RP");
        previous.off("SS");
        previous.off("TP");
        previous.off("OI");
        previous.off("DisconnectedFromServer");
      } catch {
        /* ignore */
      }
      previous.stop().catch(() => {
        /* já está caindo; nada a fazer */
      });
    }

    // Remove existing event handlers to prevent duplicates on hot reload/reconnection
    conn.off("HB");
    conn.off("Q");
    conn.off("B");
    conn.off("NM");
    conn.off("RP");
    conn.off("SS");
    conn.off("TP");
    conn.off("OI");
    conn.off("DisconnectedFromServer");

    conn.onclose((error) => md._handleSocketClose(conn, error));
    md.signalRConnection.on("HB", () => {
      md.lastHeartbeat = Date.now();
      md._maybeRefreshSymbolsOnHeartbeat();
    });

    // Initialize Web Worker for quote processing (offloads coalescing from main thread)
    // Worker handles: rounding, OHLC accumulation, bar boundary detection, throttling
    // Main thread only receives coalesced quotes at ~4/sec per symbol
    md.initQuoteWorker();

    // SignalR "Q" handler - minimal work, just forward to worker.
    // Body extracted to _onQuoteFromSocket so tests can drive the same path.
    md.signalRConnection.on("Q", md._onQuoteFromSocket);
    // BookByPrice "B" handler — forwards raw payload to receivers.
    // Phase 2 does NOT parse, merge, cache, or apply BP/UBP/HBP/RBP semantics.
    // Phase 3's central model layer consumes these via OnBookByPriceRaw(book).
    md.signalRConnection.on("B", (book) => {
      md.lastHeartbeat = Date.now();
      md.broadcastBookByPriceRawToReceivers(book);
    });
    md.signalRConnection.on("NM", (message) => {
      // News Message received from server
      console.log(`[${new Date().toISOString()}] 📰 News message received:`, {
        id: message.Id,
        type: message.Type,
        title: message.Title,
      });

      // Dynamically import newsStore to avoid circular dependency
      import("../../store/newsStore")
        .then(({ useNewsStore }) => {
          const addMessage = useNewsStore.getState().addMessage;
          addMessage(message);
        })
        .catch((err) => {
          console.error("[Marketdata] Failed to load newsStore:", err);
        });
    });
    // "RP" — preços referenciais B3 (#437; carona na assinatura de quote, sem
    // registro próprio). ~dezenas/dia/símbolo: fora do quoteWorker de propósito
    // (o worker existe para coalescing de quote; RP não precisa e não deve pagar
    // a latência). Mesmo padrão do "NM": dynamic import evita ciclo.
    md.signalRConnection.on("RP", (message) => {
      import("../../store/referentialPriceStore")
        .then(({ useReferentialPriceStore }) => {
          useReferentialPriceStore.getState().applyReferentialPrice(message);
        })
        .catch((err) => {
          console.error(
            "[Marketdata] Failed to load referentialPriceStore:",
            err,
          );
        });
    });
    // "SS"/"TP"/"OI" — fase, teórico e contratos em aberto (marketdata#25).
    // Mesmo padrão do "RP": carona na assinatura de quote, fora do quoteWorker,
    // dynamic import evita ciclo. Volumetria baixa (fase por evento, teórico só
    // em leilão, OI ~1×/dia) + enxurrada do acervo no (re)subscribe.
    md.signalRConnection.on("SS", (message) => {
      import("../../store/stateSignalsStore")
        .then(({ useStateSignalsStore }) => {
          useStateSignalsStore.getState().applySymbolState(message);
        })
        .catch((err) => {
          console.error("[Marketdata] Failed to load stateSignalsStore:", err);
        });
    });
    md.signalRConnection.on("TP", (message) => {
      import("../../store/stateSignalsStore")
        .then(({ useStateSignalsStore }) => {
          useStateSignalsStore.getState().applyTheoreticalOpening(message);
        })
        .catch((err) => {
          console.error("[Marketdata] Failed to load stateSignalsStore:", err);
        });
    });
    md.signalRConnection.on("OI", (message) => {
      import("../../store/stateSignalsStore")
        .then(({ useStateSignalsStore }) => {
          useStateSignalsStore.getState().applyOpenInterest(message);
        })
        .catch((err) => {
          console.error("[Marketdata] Failed to load stateSignalsStore:", err);
        });
    });
    md.signalRConnection.on("DisconnectedFromServer", (from, message, reason) =>
      md._handleDisconnectedFromServer(from, message, reason),
    );
    md.lastHeartbeat = Date.now();

    console.log(
      `[${new Date().toISOString()}] 🚀 Starting SignalR connection...`,
    );
    const connectionAttemptStart = Date.now();
    await md.signalRConnection.start();
    const connectionDuration = Date.now() - connectionAttemptStart;
    console.log(
      `[${new Date().toISOString()}] ✅ SignalR connection established in ${connectionDuration}ms`,
    );

    // Mark server as healthy in URL manager
    const urls = parsedMarketdataUrls[md.broker] || [];
    if (urls.length > 0) {
      urlManager.markSuccess(md.broker, "marketdata");
    }

    // Log de sucesso para o usuário
    logger.connection.success("Conexão estabelecida com servidor de dados", {
      duration: connectionDuration,
      server: md.getUrl(),
    });

    console.log(
      `[${new Date().toISOString()}] 🔄 Re-registering all symbols...`,
    );
    md.reregisterAllSymbols();
    md.reregisterAllBookByPriceSymbols();

    if (md.refreshTokenIntervalId !== 0) {
      clearInterval(md.refreshTokenIntervalId);
      md.refreshTokenIntervalId = 0;
    }

    // Proactive token refresh every 2 minutes with race condition protection
    // API tokens expire after 30 minutes, proactive refresh ensures continuous connectivity
    console.log(
      `[${new Date().toISOString()}] ⏰ Setting up proactive token refresh interval (2 minutes)`,
    );
    md.refreshTokenIntervalId = setInterval(
      async () => {
        // Race condition protection - prevent concurrent refresh attempts
        if (md.isRefreshingToken) {
          console.log(
            `[${new Date().toISOString()}] ⏳ Token refresh already in progress, skipping`,
          );
          return;
        }

        md.isRefreshingToken = true;
        try {
          console.log(
            `[${new Date().toISOString()}] 🔄 Proactive token refresh`,
          );
          await md.refreshToken();
          const expirationDate = md.parseTokenExpiration(md.signalRToken);
          console.log(
            `[${new Date().toISOString()}] ✅ Token refreshed, expires: ${expirationDate?.toISOString()}`,
          );
          md.consecutiveAuthFailures = 0; // Reset on success
        } catch (error) {
          const errorMsg = error.message || error.toString();
          // Check for non-recoverable authentication errors
          if (
            errorMsg.includes("Forbidden") ||
            errorMsg.includes("403") ||
            errorMsg.includes("401")
          ) {
            md.consecutiveAuthFailures++;
            console.error(
              `[${new Date().toISOString()}] 🚫 Token refresh auth error (${md.consecutiveAuthFailures}/5): ${errorMsg}`,
            );

            // Only log to dashboard after 5 consecutive failures (10 min of continuous failure)
            // This reduces noise from intermittent ~17% failure rate
            if (md.consecutiveAuthFailures === 5) {
              logger.auth.error(
                "Token refresh failed - auth may have expired",
                {
                  error: errorMsg,
                  broker: md.broker,
                  consecutiveFailures: md.consecutiveAuthFailures,
                },
              );
            }
            // Don't attempt further refreshes - auth string is likely expired
            // User will need to get a new auth link from broker
          } else {
            console.error(
              `[${new Date().toISOString()}] ⚠️ Token refresh failed, will retry: ${errorMsg}`,
            );
          }
        } finally {
          md.isRefreshingToken = false;
        }
      },
      2 * 60 * 1000,
    ); // Proactive refresh every 2 minutes (API tokens expire after 30 minutes)

    md.state = "online";
    // Start per-symbol staleness monitor (detects single-symbol freeze)
    md.startQuoteStalenessMonitor();
    // Reset reconnection attempts and connection errors on successful connection
    md.resetReconnectionAttempts();
    md.consecutiveConnectionErrors = 0;

    const totalConnectionTime = Date.now() - Date.parse(connectionStartTime);
    console.log(
      `[${new Date().toISOString()}] 🎉 SignalR connection process completed successfully in ${totalConnectionTime}ms`,
    );
    console.log(`[${new Date().toISOString()}] 📈 Connection stats:`, {
      totalTime: totalConnectionTime,
      connectionTime: connectionDuration,
      broker: md.broker,
      serverUrl: md.getUrl(),
      symbolsCount: md.symbols.size,
    });
    if (md.receivers.length > 0) {
      timeoutManager.setTimeout(
        () => {
          const r = md.receivers.slice(0);
          r.forEach((receiver) => {
            if (receiver.OnConnected !== undefined) receiver.OnConnected();
          });
        },
        0,
        "marketdata-connected-notification",
      );
    }
  } catch (e) {
    const failureTime = new Date().toISOString();
    const totalAttemptTime = Date.now() - Date.parse(connectionStartTime);

    console.error(
      `[${failureTime}] ❌ SignalR connection failed after ${totalAttemptTime}ms`,
    );
    console.error(`[${failureTime}] 🚨 Connection error details:`, {
      error: e.message || e,
      broker: md.broker,
      serverUrl: md.getUrl(),
      attemptDuration: totalAttemptTime,
    });

    // Log de erro para o usuário
    logger.connection.error("Falha na conexão com servidor de dados", {
      error: e.message || e,
      server: md.getUrl(),
      duration: totalAttemptTime,
    });

    // Note: Load balancing is handled by startUsingCredentials(), not here
    // This prevents infinite loops from nested retry logic

    if (e.message) {
      if (e.message.toLowerCase().indexOf("forbidden") !== -1) {
        console.error(
          `[${failureTime}] 🔐 Authentication failed - invalid credentials`,
        );
        utilities.errorHandler(
          "Conexão negada! Sua autenticação é inválida ou está expirada",
        );
      } else {
        console.error(
          `[${failureTime}] 🌐 Network/Server error during connection`,
        );

        // Check if this is a recoverable error for initial connection
        const isRecoverableError = md.isRecoverableError(e);
        console.log(
          `[${failureTime}] 🔍 Initial connection error recoverable: ${isRecoverableError}`,
        );

        if (
          isRecoverableError &&
          md.reconnectionAttempts < md.maxReconnectionAttempts
        ) {
          const delay = md.getReconnectionDelay();
          console.log(
            `[${failureTime}] 🔄 Initial connection failed, scheduling reconnection attempt ${md.reconnectionAttempts + 1}/${md.maxReconnectionAttempts} in ${delay / 1000} seconds`,
          );

          timeoutManager.setTimeout(async () => {
            const attemptNumber = md.reconnectionAttempts + 1;
            const attemptTimestamp = new Date().toISOString();

            console.log(
              `[${attemptTimestamp}] 🔄 Starting initial connection attempt ${attemptNumber}/${md.maxReconnectionAttempts}`,
            );
            console.log(
              `[${attemptTimestamp}] ⏱️  Delay since last attempt: ${delay / 1000} seconds`,
            );
            console.log(
              `[${attemptTimestamp}] 📊 Current reconnection stats:`,
              {
                attempt: attemptNumber,
                maxAttempts: md.maxReconnectionAttempts,
                delayMs: delay,
                broker: md.broker,
                serverUrl: md.getUrl(),
                triggerType: "initial_connection_failure",
                errorType: e.message,
              },
            );

            const connectivityCheckStart = Date.now();
            const isConnected = await md.checkConnectivity();
            const connectivityCheckDuration =
              Date.now() - connectivityCheckStart;

            if (isConnected) {
              console.log(
                `[${new Date().toISOString()}] ✅ Network connectivity confirmed (${connectivityCheckDuration}ms)`,
              );
              console.log(
                `[${new Date().toISOString()}] 🚀 Initiating initial connection attempt to ${md.getUrl()}`,
              );

              // Increment attempts before actual connection attempt
              md.reconnectionAttempts++;

              try {
                await md.startUsingAuth(md.broker, md.auth);
                console.log(
                  `[${new Date().toISOString()}] 🎉 Initial connection attempt ${attemptNumber} successful!`,
                );

                // Reload all charts data after successful initial connection
                await md.reloadAllChartsData();
              } catch (reconnectError) {
                console.error(
                  `[${new Date().toISOString()}] ❌ Initial connection attempt ${attemptNumber} failed:`,
                  reconnectError,
                );
                // Continue with next attempt if we haven't reached max attempts
                if (md.reconnectionAttempts < md.maxReconnectionAttempts) {
                  console.log(
                    `[${new Date().toISOString()}] ⏭️  Preparing next initial connection attempt...`,
                  );
                }
              }
            } else {
              console.warn(
                `[${new Date().toISOString()}] 🚫 Network connectivity check failed (${connectivityCheckDuration}ms)`,
              );
              console.log(
                `[${new Date().toISOString()}] ⏳ Skipping initial connection attempt ${attemptNumber}, will retry in ${delay / 1000} seconds`,
              );
              // Increment attempts even for connectivity issues to prevent infinite loop
              md.reconnectionAttempts++;

              // Check if we still have attempts left
              if (md.reconnectionAttempts < md.maxReconnectionAttempts) {
                // Try again after the same delay
                timeoutManager.setTimeout(
                  () => {
                    console.log(
                      `[${new Date().toISOString()}] 🔄 Retrying initial connection after connectivity check failure...`,
                    );
                    md.startUsingAuth(md.broker, md.auth).catch((err) => {
                      console.error(
                        `[${new Date().toISOString()}] ❌ Retry after connectivity failure failed:`,
                        err,
                      );
                    });
                  },
                  delay,
                  "marketdata-initial-connection-retry",
                );
              } else {
                console.error(
                  `[${new Date().toISOString()}] ❌ Maximum reconnection attempts reached after connectivity failures`,
                );
                utilities.errorHandler(
                  "Falha ao conectar após várias tentativas. Verifique sua conexão de internet e a disponibilidade do servidor.",
                );
              }
            }
          }, delay);
        } else if (!isRecoverableError) {
          console.log(
            `[${failureTime}] 🚫 Initial connection error is not recoverable, showing error to user`,
          );
          utilities.errorHandler(e);
        } else {
          console.error(
            `[${failureTime}] Maximum reconnection attempts (${md.maxReconnectionAttempts}) reached for initial connection. Giving up.`,
          );
          utilities.errorHandler(
            "Falha ao conectar após várias tentativas. Verifique sua conexão de internet.",
          );
        }
      }
    } else {
      console.log(
        `[${failureTime}] ⏳ Unknown connection error, checking if should retry...`,
      );

      // Check if we still have reconnection attempts left
      if (md.reconnectionAttempts < md.maxReconnectionAttempts) {
        md.reconnectionAttempts++;
        const delay = md.getReconnectionDelay();

        console.log(
          `[${failureTime}] 🔄 Scheduling retry attempt ${md.reconnectionAttempts}/${md.maxReconnectionAttempts} in ${delay / 1000} seconds`,
        );
        timeoutManager.setTimeout(
          () => {
            console.log(
              `[${new Date().toISOString()}] 🔄 Retrying connection after unknown error...`,
            );
            md.startUsingAuth(md.broker, md.auth).catch((err) => {
              console.error(
                `[${new Date().toISOString()}] ❌ Retry after unknown error failed:`,
                err,
              );
            });
          },
          delay,
          "marketdata-unknown-error-retry",
        );
      } else {
        console.error(
          `[${failureTime}] ❌ Maximum reconnection attempts reached after unknown errors`,
        );
        utilities.errorHandler(
          "Falha ao conectar ao servidor após várias tentativas.",
        );
      }
    }
  }
};

export const startUsingCredentials = async (md, broker, user, pwd) => {
  // #304: if this tab was evicted by the server, refuse to log back in. The user
  // must close the tab and open a fresh one — no F5 / re-login recovery.
  if (readEvictedFlag()) {
    md.closedByServer = true;
    console.log(
      `[${new Date().toISOString()}] 🔌 startUsingCredentials blocked: tab is in evicted state (sessionStorage). Open a new tab to reconnect.`,
    );
    return;
  }
  md.broker = broker;
  // Clear any prior server-eviction flag — explicit user login resets the state. (#304)
  md.closedByServer = false;
  const urls = parsedMarketdataUrls[md.broker] || [];

  // Try all servers sequentially until one succeeds
  let lastError = null;
  const maxServers = urls.length || 1;

  for (let attempt = 0; attempt < maxServers; attempt++) {
    try {
      // Encode parameters to handle special characters
      const encodedPwd = encodeURIComponent(pwd);
      const encodedProduct = encodeURIComponent(PRODUCT_NAME);
      const encodedVersion = encodeURIComponent(PRODUCT_VERSION);
      const serviceUrl = `${md.getUrl()}${AUTH_COMMAND}?broker=${broker}&user=${user}&pwd=${encodedPwd}&product=${encodedProduct}&version=${encodedVersion}`;
      console.log(
        `[${new Date().toISOString()}] 🔐 Attempting auth on server ${urlManager.getCurrentIndex(broker, "marketdata")}: ${md.getUrl()} (${PRODUCT_NAME} v${PRODUCT_VERSION})`,
      );

      const response = await utilities.getStringFromURL(serviceUrl, null);
      md.auth = response;

      // Mark success and connect
      if (urls.length > 0) {
        urlManager.markSuccess(md.broker, "marketdata");
      }

      await md.runConnection();
      return; // Success!
    } catch (e) {
      lastError = e;
      console.error(
        `[${new Date().toISOString()}] ❌ Auth failed on ${md.getUrl()}:`,
        e.message,
      );

      // If multiple servers configured, try next one
      if (urls.length > 1 && attempt < maxServers - 1) {
        const nextUrl = urlManager.markFailureAndGetNext(
          md.broker,
          "marketdata",
          urls,
          e,
        );
        if (nextUrl) {
          console.log(
            `[${new Date().toISOString()}] 🔄 Trying next server: ${nextUrl}`,
          );
          // Small delay to avoid hammering servers
          await new Promise((resolve) => setTimeout(resolve, 500));
          continue; // Try next server
        }
      }

      // Last server or single server - break and show error
      break;
    }
  }

  // All servers failed - show error
  if (lastError) {
    if (lastError.message && lastError.message === "Forbidden") {
      utilities.errorHandler("Credenciais inválidas!");
    } else {
      utilities.errorHandler(lastError);
    }
  }
};

export const startUsingAuth = async (md, broker, auth) => {
  // #304: sticky eviction blocks reconnect — see startUsingCredentials.
  if (readEvictedFlag()) {
    md.closedByServer = true;
    console.log(
      `[${new Date().toISOString()}] 🔌 startUsingAuth blocked: tab is in evicted state (sessionStorage).`,
    );
    return;
  }
  md.connectionId++;
  const connId = md.connectionId;
  window.__MARKETDATA_CONNECTION_ID__ = connId;
  const startTime = new Date().toISOString();
  console.log(
    `[${startTime}] 🔌 [conn#${connId}] Starting authentication and connection process for broker: ${broker}`,
  );

  md.broker = broker;
  md.auth = auth;
  const urls = parsedMarketdataUrls[md.broker] || [];

  // Try all servers sequentially until one succeeds
  let lastError = null;
  const maxServers = urls.length || 1;

  for (let attempt = 0; attempt < maxServers; attempt++) {
    try {
      console.log(
        `[${new Date().toISOString()}] 🔐 Attempting broker auth on server ${urlManager.getCurrentIndex(broker, "marketdata") + 1}/${maxServers}: ${md.getUrl()}`,
      );

      await md.runConnection();

      // Success! Mark server as healthy
      if (urls.length > 0) {
        urlManager.markSuccess(md.broker, "marketdata");
      }

      console.log(
        `[${new Date().toISOString()}] ✅ Broker auth successful on ${md.getUrl()}`,
      );
      return; // Success!
    } catch (e) {
      lastError = e;
      console.error(
        `[${new Date().toISOString()}] ❌ Broker auth failed on ${md.getUrl()}:`,
        e.message,
      );

      // If multiple servers configured, try next one
      if (urls.length > 1 && attempt < maxServers - 1) {
        const nextUrl = urlManager.markFailureAndGetNext(
          md.broker,
          "marketdata",
          urls,
          e,
        );
        if (nextUrl) {
          console.log(
            `[${new Date().toISOString()}] 🔄 Trying next marketdata server: ${nextUrl}`,
          );
          // Small delay to avoid hammering servers
          await new Promise((resolve) => setTimeout(resolve, 500));
          continue; // Try next server
        }
      }

      // Last server or single server - break and throw
      break;
    }
  }

  // All servers failed - throw error
  console.error(
    `[${new Date().toISOString()}] ❌ Broker auth failed on all ${maxServers} marketdata servers`,
  );
  throw lastError || new Error("Broker authentication failed on all servers");
};

// ── Quote worker lifecycle (delegado p/ quoteWorkerLifecycle.js) ──────────
// Init/terminate do worker, adaptive throttling por FPS e staleness monitor.
// Contrato: quoteWorkerLifecycleContract.test.js. Estado segue na instância.

export const stop = (md) => {
  if (md.signalRConnection) {
    // Remove all event handlers before stopping
    md.signalRConnection.off("HB");
    md.signalRConnection.off("Q");
    md.signalRConnection.off("B");
    md.signalRConnection.off("NM");
    md.signalRConnection.off("RP");
    md.signalRConnection.off("SS");
    md.signalRConnection.off("TP");
    md.signalRConnection.off("OI");
    md.signalRConnection.off("DisconnectedFromServer");
    md.signalRConnection.off("close");

    md.signalRConnection.stop();
  }
  // Terminate quote worker
  md.terminateQuoteWorker();
  // Stop staleness monitor and clear tracking data
  md.stopQuoteStalenessMonitor();
  md.lastQuotes.clear();
  // Clean up token refresh interval (prevent leak on stop without reconnect)
  if (md.refreshTokenIntervalId !== 0) {
    clearInterval(md.refreshTokenIntervalId);
    md.refreshTokenIntervalId = 0;
  }
  md.state = "offline";
  // Reset reconnection attempts when manually stopped
  md.resetReconnectionAttempts();
  // Clear all pending timeouts
  timeoutManager.clearAllTimeouts("marketdata-stop");
};

export const getReconnectionDelay = (md) => {
  const exp = md.baseReconnectionDelay * Math.pow(2, md.reconnectionAttempts);
  const capped = Math.min(exp, md.maxReconnectionDelay);
  // Equal jitter: de-synchronize mass reconnects (server can drop many slow
  // clients at once → avoid a thundering-herd reconnect wave). Floor at 50% of
  // the capped delay, randomize the other 50%.
  return Math.round(capped * (0.5 + Math.random() * 0.5));
};

// Extraído de runConnection p/ testabilidade do P0 de eviction (#304).
export const _handleDisconnectedFromServer = (md, from, message, reason) => {
  if (reason === "slow-consumer") {
    // Phase 2: dropped for slow consumption — recoverable. Reconnect like a clean
    // disconnect; do NOT evict. onclose(undefined) passes the reconnect guard
    // (error===undefined arm) because closedByServer stays false.
    md.slowConsumerReconnects += 1;
    console.warn(
      `[${new Date().toISOString()}] ⚠️ Dropped as slow-consumer (count: ${md.slowConsumerReconnects}) — reconnecting`,
    );
    md.resetReconnectionAttempts(); // ensure a fresh reconnect budget
    md.signalRConnection.stop(); // → onclose(undefined) → recoverable reconnect
    return;
  }
  // Session eviction (reason absent OR "evicted") — permanent. UNCHANGED behavior.
  md.signalRConnection.stop();
  md.state = "offline";
  md.closedByServer = true;
  writeEvictedFlag(); // #304: persist across F5 — this tab is permanently dead.
  timeoutManager.setTimeout(() => {
    if (message === undefined) {
      message = "Você foi desconectado do servidor de marketdata.";
    }
    utilities.errorHandler(message);
  }, 0);
};

// Extraído de runConnection p/ testabilidade dos P0 de reconexão: identity-guard
// (ignora close de socket obsoleto) + não-reset de reconnectionAttempts no close.
export const _handleSocketClose = (md, conn, error) => {
  const timestamp = new Date().toISOString();
  // C2/A1: se uma conexão mais nova já assumiu (runConnection substituiu esta), este close é
  // de um socket obsoleto → ignora, senão dispara um reconnect concorrente com a conexão atual.
  if (md.signalRConnection !== conn) {
    console.log(
      `[${timestamp}] Stale onclose ignored — connection was already replaced.`,
    );
    return;
  }
  console.log(`[${timestamp}] SignalR connection closed. State: ${md.state}`);

  md.state = "offline";

  if (error !== undefined) {
    md.consecutiveConnectionErrors++;
    console.error(
      `[${timestamp}] SignalR connection error (${md.consecutiveConnectionErrors}/3):`,
      error,
    );

    // Check if this is a recoverable error
    const isRecoverableError = md.isRecoverableError(error);
    console.log(`[${timestamp}] 🔍 Error recoverable: ${isRecoverableError}`);

    if (isRecoverableError) {
      console.log(
        `[${timestamp}] 🔄 Error is recoverable, attempting reconnection...`,
      );

      // Only log to dashboard after 3 consecutive connection errors
      // This reduces noise from intermittent network issues that auto-recover
      if (md.consecutiveConnectionErrors === 3) {
        logger.connection.error("Conexão perdida", {
          error: error.message,
          broker: md.broker,
          server: md.getUrl(),
          consecutiveErrors: md.consecutiveConnectionErrors,
        });
      }
    } else {
      console.log(
        `[${timestamp}] 🚫 Error is not recoverable, showing error to user`,
      );
      // Always log non-recoverable errors immediately
      logger.connection.error("Erro não recuperável na conexão", {
        error: error.message,
        broker: md.broker,
        consecutiveErrors: md.consecutiveConnectionErrors,
      });
      utilities.errorHandler(error);
    }
  } else {
    logger.connection.info("Conexão fechada normalmente", {
      broker: md.broker,
      server: md.getUrl(),
    });
  }
  // Attempt reconnection if:
  // 1. No error (clean disconnect), OR
  // 2. Error is recoverable AND not explicitly closed by server
  const shouldAttemptReconnection =
    (error === undefined ||
      (error !== undefined && md.isRecoverableError(error))) &&
    !md.closedByServer;

  if (shouldAttemptReconnection) {
    if (md.reconnectionAttempts < md.maxReconnectionAttempts) {
      const delay = md.getReconnectionDelay();
      console.log(
        `[${timestamp}] Reconnection attempt ${md.reconnectionAttempts + 1}/${md.maxReconnectionAttempts} scheduled in ${delay / 1000} seconds`,
      );

      timeoutManager.setTimeout(async () => {
        const attemptNumber = md.reconnectionAttempts + 1;
        const attemptTimestamp = new Date().toISOString();

        console.log(
          `[${attemptTimestamp}] 🔄 Starting reconnection attempt ${attemptNumber}/${md.maxReconnectionAttempts}`,
        );
        console.log(
          `[${attemptTimestamp}] ⏱️  Delay since last attempt: ${delay / 1000} seconds`,
        );
        console.log(`[${attemptTimestamp}] 📊 Current reconnection stats:`, {
          attempt: attemptNumber,
          maxAttempts: md.maxReconnectionAttempts,
          delayMs: delay,
          broker: md.broker,
          serverUrl: md.getUrl(),
          triggerType: error ? "error_recovery" : "clean_disconnect",
          errorType: error ? error.message : null,
        });

        const connectivityCheckStart = Date.now();
        const isConnected = await md.checkConnectivity();
        const connectivityCheckDuration = Date.now() - connectivityCheckStart;

        if (isConnected) {
          console.log(
            `[${new Date().toISOString()}] ✅ Network connectivity confirmed (${connectivityCheckDuration}ms)`,
          );
          console.log(
            `[${new Date().toISOString()}] 🚀 Initiating SignalR reconnection to ${md.getUrl()}`,
          );

          // Increment attempts before actual reconnection
          md.reconnectionAttempts++;

          try {
            await md.startUsingAuth(md.broker, md.auth);
            console.log(
              `[${new Date().toISOString()}] 🎉 Reconnection attempt ${attemptNumber} successful!`,
            );
            md.consecutiveConnectionErrors = 0; // Reset on successful reconnection

            // Log de sucesso de reconexão para o usuário
            logger.connection.success(
              "Reconexão bem-sucedida com servidor de dados",
              {
                attempt: attemptNumber,
                server: md.getUrl(),
              },
            );

            // Reload all charts data after successful reconnection
            await md.reloadAllChartsData();
          } catch (reconnectError) {
            console.error(
              `[${new Date().toISOString()}] ❌ Reconnection attempt ${attemptNumber} failed:`,
              reconnectError,
            );
            // Continue with next attempt if we haven't reached max attempts
            if (md.reconnectionAttempts < md.maxReconnectionAttempts) {
              console.log(
                `[${new Date().toISOString()}] ⏭️  Preparing next reconnection attempt...`,
              );
            }
          }
        } else {
          console.warn(
            `[${new Date().toISOString()}] 🚫 Network connectivity check failed (${connectivityCheckDuration}ms)`,
          );
          console.log(
            `[${new Date().toISOString()}] ⏳ Skipping reconnection attempt ${attemptNumber}, will retry in ${delay / 1000} seconds`,
          );
          // Increment attempts even for connectivity issues to prevent infinite loop
          md.reconnectionAttempts++;

          // Check if we still have attempts left
          if (md.reconnectionAttempts < md.maxReconnectionAttempts) {
            // Try again after the same delay
            timeoutManager.setTimeout(() => {
              console.log(
                `[${new Date().toISOString()}] 🔄 Retrying reconnection after connectivity check failure...`,
              );
              md.startUsingAuth(md.broker, md.auth).catch((err) => {
                console.error(
                  `[${new Date().toISOString()}] ❌ Retry after connectivity failure failed:`,
                  err,
                );
              });
            }, delay);
          } else {
            console.error(
              `[${new Date().toISOString()}] ❌ Maximum reconnection attempts reached after connectivity failures`,
            );
            utilities.errorHandler(
              "Falha ao conectar após várias tentativas. Verifique sua conexão de internet e a disponibilidade do servidor.",
            );
          }
        }
      }, delay);
    } else {
      console.error(
        `[${timestamp}] Maximum reconnection attempts (${md.maxReconnectionAttempts}) reached. Giving up.`,
      );
      // Only show error to user if it's a non-recoverable error or no error occurred
      if (
        error === undefined ||
        (error !== undefined && !md.isRecoverableError(error))
      ) {
        utilities.errorHandler(
          error ||
            "Falha ao reconectar após várias tentativas. Verifique sua conexão de internet.",
        );
      } else {
        utilities.errorHandler(
          "Falha ao reconectar após várias tentativas. Verifique sua conexão de internet.",
        );
      }
    }
  } else {
    console.log(
      `[${timestamp}] Server explicitly disconnected. Resetting reconnection attempts.`,
    );
    // Server explicitly disconnected us, reset attempts for next time
    md.resetReconnectionAttempts();
  }

  // Always notify receivers when connection is lost, regardless of the reason
  if (md.receivers.length > 0) {
    timeoutManager.setTimeout(() => {
      const r = md.receivers.slice(0);
      r.forEach((receiver) => {
        if (receiver.OnDisconnected !== undefined) receiver.OnDisconnected();
      });
    }, 0);
  }

  // closedByServer is intentionally NOT reset here. It persists until an
  // explicit user-initiated login clears it in startUsingCredentials.
  // This blocks auto-reconnect paths (e.g. page visibility hook) after a
  // server-side eviction ("Cliente se conectou em outro endpoint"). (#304)
};

export const resetReconnectionAttempts = (md) => {
  md.reconnectionAttempts = 0;
};

export const isRecoverableError = (md, error) => {
  if (!error) return false;

  const errorMessage = error.message || error.toString();

  // WebSocket 1006 (no reason given) - usually recoverable
  if (errorMessage.includes("1006")) return true;

  // Network-related errors that are usually recoverable
  if (errorMessage.includes("NetworkError")) return true;
  if (errorMessage.includes("Failed to fetch")) return true;
  if (errorMessage.includes("connection")) return true;
  if (errorMessage.includes("timeout")) return true;

  // Authentication errors are NOT recoverable
  if (errorMessage.includes("401")) return false;
  if (errorMessage.includes("403")) return false;
  if (errorMessage.includes("Forbidden")) return false;
  if (errorMessage.includes("Unauthorized")) return false;

  // Default to recoverable for unknown errors
  return true;
};

export const checkConnectivity = async (md) => {
  try {
    // Try to fetch the marketdata symbols endpoint as a connectivity test
    // This is a lightweight endpoint that should exist on most marketdata servers
    // Use getValidToken (not the raw signalRToken) so an expired token is
    // refreshed first — otherwise the check 401s and is misread as "no network",
    // skipping reconnection until the background refresh timer happens to fire.
    const token = await md.getValidToken();
    const testUrl = `${md.getUrl()}/api/marketdata/symbols`;
    const response = await fetch(testUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });
    return response.ok;
  } catch (error) {
    console.log("Connectivity check failed:", error.message);
    return false;
  }
};

// (não usa a instância — dispara via event bus; assinatura uniforme do módulo)
// eslint-disable-next-line no-unused-vars
export const reloadAllChartsData = async (_md) => {
  try {
    console.log(
      `[${new Date().toISOString()}] 🔄 Reloading historical data for all charts after reconnection...`,
    );

    // Emit event to request chart data reload (breaks circular dependency)
    eventBus.emit(EVENTS.CHART_DATA_RELOAD_REQUESTED);

    console.log(
      `[${new Date().toISOString()}] 📡 Chart data reload requested via event bus`,
    );
  } catch (error) {
    console.error(
      `[${new Date().toISOString()}] ❌ Error requesting chart data reload:`,
      error,
    );
  }
};

export const reregisterAllSymbols = (md) => {
  const symbols = Array.from(md.registeredSymbols.keys());
  if (symbols.length > 0) {
    logger.diagnostic.info(
      `Re-registering ${symbols.length} symbols after reconnect`,
      {
        event: "reregister-all",
        count: symbols.length,
        symbols,
      },
    );
  }
  md.registeredSymbols.forEach((value, key) => {
    const symbol = key;
    if (md.signalRConnection && md.signalRConnection.send) {
      md.signalRConnection.send("register", "q", symbol);
      md.registeredSymbols.set(symbol, null);
    }
  });
};

/**
 * Register a RAW (pre-coalescing) per-trade quote tap for `symbol`.
 *
 * Used only by the history↔RT seam reconcile: while a chart loads history,
 * its tap buffers raw trades so the seam bar can be deduped by order number
 * (ON) — coalesced quotes sum volume and can't be split at the snapshot
 * boundary. Family-aware (a WINM26 tap receives WINFUT trades).
 *
 * @param {string} symbol - chart symbol to match (exact or same family)
 * @param {(quote:Object)=>void} cb - called synchronously per raw trade
 * @returns {()=>void} unregister function (idempotent)
 */
